import type { Rect } from '@view-model/models/common/basic/Rect';
import { Point } from '@view-model/models/common/basic';
import type { ICalloutTarget } from './ICalloutTarget';

/**
 * 楕円形のCalloutターゲット。
 * バウンディングボックスに内接する楕円として、しっぽ先端の交点を計算する。
 *
 * 楕円の方程式: (x/a)² + (y/b)² = 1 (中心を原点とした場合)
 * 中心から方向(ndx, ndy)への交点: t = 1 / √(ndx²/a² + ndy²/b²)
 */
export class EllipseTarget implements ICalloutTarget {
  private readonly effectiveRect: Rect;

  /**
   * @param boundingRect バウンディングボックス
   * @param margin 楕円境界からのマージン（外側方向に広げる量）。TargetRect と同様に矢印先端を境界から離すために使用する
   */
  constructor(boundingRect: Rect, margin: number = 0) {
    this.effectiveRect = margin !== 0 ? boundingRect.applyMarginKeepingCenter(margin) : boundingRect;
  }

  intersectPointFromCenter(dest: Point): Point {
    const cx = this.effectiveRect.getCenterX();
    const cy = this.effectiveRect.getCenterY();
    const a = this.effectiveRect.width / 2;
    const b = this.effectiveRect.height / 2;

    const dx = dest.x - cx;
    const dy = dest.y - cy;
    const len = Math.sqrt(dx * dx + dy * dy);

    if (len === 0) return new Point(cx, cy);

    const ndx = dx / len;
    const ndy = dy / len;
    const t = 1 / Math.sqrt((ndx * ndx) / (a * a) + (ndy * ndy) / (b * b));

    return new Point(cx + ndx * t, cy + ndy * t);
  }
}
